import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Database, RefreshCw, Search, Save, Trash2, X, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE = 25;
// Collections where an accidental delete is especially costly — require typing DELETE.
const HIGH_RISK = new Set(['users', 'bookings', 'services', 'jobs', 'escrow_payments', 'invoices', 'reviews']);

const DatabaseTab = ({ canEdit = true }) => {
  const [collections, setCollections] = useState([]);
  const [selected, setSelected] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // {id, json}
  const [jsonText, setJsonText] = useState('');
  const [jsonErr, setJsonErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // {doc, id}
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const loadCollections = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/owner/db/collections`);
      setCollections(r.data.collections || []);
    } catch (e) { toast.error('Failed to load collections'); }
  }, []);

  useEffect(() => { loadCollections(); }, [loadCollections]);

  const loadDocs = useCallback(async (coll, newSkip = 0, search = '') => {
    if (!coll) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/owner/db/${coll}`, { params: { skip: newSkip, limit: PAGE, q: search } });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setSkip(newSkip);
    } catch (e) { toast.error('Failed to load documents'); }
    finally { setLoading(false); }
  }, []);

  const openCollection = (name) => { setSelected(name); setQ(''); loadDocs(name, 0, ''); };

  const openEditor = (doc) => {
    const id = doc.id || doc._id;
    setEditing({ id });
    const { _id, ...rest } = doc;
    setJsonText(JSON.stringify(rest, null, 2));
    setJsonErr('');
  };

  const saveDoc = async () => {
    let parsed;
    try { parsed = JSON.parse(jsonText); }
    catch (e) { setJsonErr('Invalid JSON: ' + e.message); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/owner/db/${selected}/${editing.id}`, { updates: parsed });
      toast.success('Document saved');
      setEditing(null);
      loadDocs(selected, skip, q);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const requestDelete = (doc) => {
    setConfirmDel({ doc, id: doc.id || doc._id });
    setConfirmText('');
  };

  const performDelete = async () => {
    if (!confirmDel) return;
    const highRisk = HIGH_RISK.has(selected);
    if (highRisk && confirmText.trim() !== 'DELETE') return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/owner/db/${selected}/${confirmDel.id}`);
      toast.success('Document deleted');
      setConfirmDel(null);
      setConfirmText('');
      loadDocs(selected, 0, q);
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
    finally { setDeleting(false); }
  };

  const preview = (doc) => {
    const label = doc.name || doc.email || doc.title || doc.service_title || doc.provider_name || doc.status || '';
    return label ? String(label).slice(0, 60) : '';
  };

  return (
    <div className="text-gray-200" data-testid="database-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2"><Database className="w-5 h-5" /> Database</h2>
        <button onClick={loadCollections} className="inline-flex items-center gap-1 text-sm text-gray-300 hover:text-white" data-testid="db-refresh-collections">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>
      <p className="text-xs text-amber-400/90 mb-4">⚠️ Direct database access — edits and deletions are immediate and permanent. Passwords are masked and cannot be edited here.</p>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Collections list */}
        <div className="md:col-span-1 bg-slate-800/60 rounded-xl p-2 max-h-[70vh] overflow-y-auto border border-slate-700" data-testid="db-collections">
          {collections.map((c) => (
            <button
              key={c.name}
              onClick={() => openCollection(c.name)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between mb-1 ${selected === c.name ? 'bg-orange-500/20 text-orange-300' : 'hover:bg-slate-700/60 text-gray-300'}`}
              data-testid={`db-collection-${c.name}`}
            >
              <span className="truncate">{c.name}</span>
              <span className="text-xs text-gray-400 ml-2">{c.count}</span>
            </button>
          ))}
          {collections.length === 0 && <p className="text-xs text-gray-400 p-3">No collections.</p>}
        </div>

        {/* Documents */}
        <div className="md:col-span-3">
          {!selected ? (
            <div className="bg-slate-800/40 rounded-xl p-8 text-center text-gray-400 border border-slate-700">Select a collection to browse its documents.</div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-3">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') loadDocs(selected, 0, q); }}
                    placeholder="Search id, email, name, title, status…"
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-3 py-2 text-sm text-gray-100"
                    data-testid="db-search-input"
                  />
                </div>
                <button onClick={() => loadDocs(selected, 0, q)} className="px-3 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold" data-testid="db-search-btn">Search</button>
              </div>

              <div className="bg-slate-800/60 rounded-xl border border-slate-700 divide-y divide-slate-700 max-h-[60vh] overflow-y-auto" data-testid="db-documents">
                {loading ? (
                  <div className="p-6 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /> Loading…</div>
                ) : items.length === 0 ? (
                  <div className="p-6 text-center text-gray-400">No documents.</div>
                ) : items.map((doc) => (
                  <div key={doc.id || doc._id} className="flex items-center justify-between gap-3 px-3 py-2.5" data-testid={`db-doc-${doc.id || doc._id}`}>
                    <button onClick={() => openEditor(doc)} className="flex-1 min-w-0 text-left" data-testid={`db-doc-edit-${doc.id || doc._id}`}>
                      <div className="text-xs text-gray-400 truncate">{doc.id || doc._id}</div>
                      {preview(doc) && <div className="text-sm text-gray-200 truncate">{preview(doc)}</div>}
                    </button>
                    <button
                      onClick={() => canEdit && requestDelete(doc)}
                      disabled={!canEdit}
                      className="p-1.5 text-red-400 hover:text-red-300 disabled:opacity-30 disabled:cursor-not-allowed"
                      title={canEdit ? 'Delete' : 'View-only access'}
                      data-testid={`db-doc-delete-${doc.id || doc._id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-3 text-sm text-gray-400">
                <span>{total} document{total === 1 ? '' : 's'}</span>
                <div className="flex items-center gap-2">
                  <button disabled={skip === 0 || loading} onClick={() => loadDocs(selected, Math.max(0, skip - PAGE), q)} className="p-1.5 disabled:opacity-40" data-testid="db-prev-page"><ChevronLeft className="w-4 h-4" /></button>
                  <span>{Math.floor(skip / PAGE) + 1} / {Math.max(1, Math.ceil(total / PAGE))}</span>
                  <button disabled={skip + PAGE >= total || loading} onClick={() => loadDocs(selected, skip + PAGE, q)} className="p-1.5 disabled:opacity-40" data-testid="db-next-page"><ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="db-editor-modal">
          <div className="bg-slate-900 rounded-xl border border-slate-700 w-full max-w-2xl flex flex-col max-h-[90vh]">
            <div className="px-5 py-3 border-b border-slate-700 flex items-center justify-between shrink-0">
              <div>
                <h3 className="font-bold text-gray-100">Edit document</h3>
                <p className="text-xs text-gray-400">{selected} · {editing.id}</p>
              </div>
              <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-white" data-testid="db-editor-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <textarea
                value={jsonText}
                onChange={(e) => { setJsonText(e.target.value); setJsonErr(''); }}
                spellCheck={false}
                readOnly={!canEdit}
                className="w-full h-[50vh] bg-slate-950 border border-slate-700 rounded-lg p-3 font-mono text-xs text-green-300"
                data-testid="db-editor-textarea"
              />
              {!canEdit && <p className="text-amber-400 text-xs mt-2">👁 View-only access — saving is disabled.</p>}
              {jsonErr && <p className="text-red-400 text-xs mt-2" data-testid="db-editor-error">{jsonErr}</p>}
            </div>
            <div className="px-5 py-3 border-t border-slate-700 flex gap-3 shrink-0">
              <button onClick={() => setEditing(null)} className="flex-1 border border-slate-600 text-gray-300 py-2 rounded-lg font-semibold hover:bg-slate-800" data-testid="db-editor-cancel">Cancel</button>
              <button onClick={saveDoc} disabled={saving || !canEdit} className="flex-1 bg-orange-500 text-white py-2 rounded-lg font-semibold hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2" data-testid="db-editor-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation — high-risk collections require typing DELETE */}
      {confirmDel && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" data-testid="db-delete-modal">
          <div className="bg-slate-900 rounded-xl border border-red-800/60 w-full max-w-md p-5">
            <div className="flex items-center gap-2 mb-2">
              <Trash2 className="w-5 h-5 text-red-400" />
              <h3 className="font-bold text-gray-100">Delete document</h3>
            </div>
            <p className="text-sm text-gray-300 mb-1">Collection: <span className="font-mono text-gray-100">{selected}</span></p>
            <p className="text-xs text-gray-400 mb-4 break-all">ID: {confirmDel.id}</p>
            {HIGH_RISK.has(selected) ? (
              <>
                <p className="text-sm text-red-300 mb-2">⚠️ This is a high-risk collection. This action is permanent and cannot be undone. Type <span className="font-bold">DELETE</span> to confirm.</p>
                <input
                  autoFocus
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && confirmText.trim() === 'DELETE') performDelete(); }}
                  placeholder="Type DELETE"
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-gray-100 mb-4"
                  data-testid="db-delete-confirm-input"
                />
              </>
            ) : (
              <p className="text-sm text-gray-300 mb-4">This action is permanent and cannot be undone.</p>
            )}
            <div className="flex gap-3">
              <button onClick={() => { setConfirmDel(null); setConfirmText(''); }} className="flex-1 border border-slate-600 text-gray-300 py-2 rounded-lg font-semibold hover:bg-slate-800" data-testid="db-delete-cancel">Cancel</button>
              <button
                onClick={performDelete}
                disabled={deleting || (HIGH_RISK.has(selected) && confirmText.trim() !== 'DELETE')}
                className="flex-1 bg-red-600 text-white py-2 rounded-lg font-semibold hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                data-testid="db-delete-confirm-btn"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseTab;
